import { Link, useSearchParams } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { blogPosts, blogCategories } from "../data/content";

const BlogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";

  const filteredPosts = activeCategory === "all" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  const handleCategoryChange = (category) => {
    if (category === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  return (
    <div data-testid="blog-page">
      <SEO path="/blog" />
      {/* Hero */}
      <section className="relative py-8 sm:py-10 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="w-12 h-1 bg-amber-500 mb-3" />
          <h1 className="text-2xl sm:text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Blog
          </h1>
          <p className="text-stone-300 mt-2 max-w-2xl text-sm">
            Hasznos tippek, inspirációk és hírek.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-stone-200 sticky top-24 sm:top-28 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-wrap gap-2 sm:gap-4">
            {blogCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium transition-colors ${
                  activeCategory === cat.id
                    ? "bg-amber-600 text-white"
                    : "bg-stone-100 text-slate-600 hover:bg-stone-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-12 sm:py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.slug}
                className="bg-white border border-stone-200 overflow-hidden group hover:shadow-lg transition-shadow"
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Link>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                    <span className="bg-amber-100 text-amber-700 px-2 py-1">
                      {post.categoryLabel}
                    </span>
                  </div>
                  <Link to={`/blog/${post.slug}`}>
                    <h2 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {post.title}
                    </h2>
                  </Link>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {post.date}
                    </div>
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="flex items-center text-amber-600 font-medium hover:gap-2 transition-all"
                    >
                      Tovább <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <p className="text-center text-slate-500 py-12">
              Nincs találat ebben a kategóriában.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
